import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LineChart, LogOut, Pause, Play, Mail, ShieldCheck, Send, Trash2, Sliders, Lock, Flame, Zap, User, UserX } from "lucide-react";
import UserSettingsModal from "@/components/UserSettingsModal";
import ErrorBoundary from "@/components/ErrorBoundary";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [defaultStrategy, setDefaultStrategy] = useState("");
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cleanupBusy, setCleanupBusy] = useState(false);
  const [cleanupStatus, setCleanupStatus] = useState(null);
  // Telegram admin settings
  const [tg, setTg] = useState(null);
  const [tgSaving, setTgSaving] = useState(false);
  const [tgTesting, setTgTesting] = useState(false);
  // Bulk-select state
  const [selectedIds, setSelectedIds] = useState([]);
  const [settingsTarget, setSettingsTarget] = useState(null);
  // Fire-all state
  const [fireAllPair, setFireAllPair] = useState("XAU_USD");
  const [fireAllSide, setFireAllSide] = useState("BUY");
  const [fireAllBusy, setFireAllBusy] = useState(false);
  const [fireAllResult, setFireAllResult] = useState(null);

  const fireForAll = async () => {
    if (!fireAllPair) return toast.error("Pick a pair first");
    const msg = `Fire ${fireAllSide} on ${fireAllPair} for ALL eligible users?\n\n` +
                `Each user uses THEIR OWN lot, SL, TP, strategy & spread buffer.\n` +
                `Users on rejection-wick or with the pair off-list are skipped.\n\n` +
                `Continue?`;
    if (!window.confirm(msg)) return;
    setFireAllBusy(true); setFireAllResult(null);
    try {
      const { data } = await api.post(
        `/admin/strategy/fire-all?pair=${encodeURIComponent(fireAllPair)}&side=${fireAllSide}`
      );
      setFireAllResult(data);
      toast.success(`Fired ${data.fired}/${data.total_users} · skipped ${data.skipped} · failed ${data.failed}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fire-all failed");
    } finally { setFireAllBusy(false); }
  };

  const load = async () => {
    try {
      const [s, u, ds, str, tgRes] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/default-strategy"),
        api.get("/strategies"),
        api.get("/admin/telegram"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setDefaultStrategy(ds.data.strategy_id || "");
      setStrategies(str.data.strategies || []);
      setTg(tgRes.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Not authorized as admin");
        nav("/admin/login");
      }
    }
  };
  useEffect(() => { load(); const i = setInterval(load, 8000); return () => clearInterval(i); }, []);

  if (user && user.role !== "admin") {
    nav("/");
    return null;
  }

  const togglePause = async (u) => {
    setLoading(true);
    try {
      await api.post(`/admin/users/${u.id}/pause`, { paused: !u.paused });
      toast.success(`${u.email} ${!u.paused ? "paused" : "resumed"}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const updateQuota = async (u, n) => {
    if (n < 0 || n > 10) return;
    try {
      await api.post(`/admin/users/${u.id}/quota`, { account_quota: n });
      toast.success(`${u.email} quota → ${n}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const forceVerify = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/verify`);
      toast.success(`${u.email} verified`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const setSubscription = async (u, newStatus) => {
    try {
      await api.post(`/admin/users/${u.id}/subscription`, { status: newStatus });
      toast.success(`${u.email} → ${newStatus}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleCertificateName = async (u) => {
    const next = !u.show_name_on_certificate;
    try {
      await api.put(`/admin/users/${u.id}/certificate-display`,
                    { show_name_on_certificate: next });
      toast.success(next
        ? `${u.email}: win cards will now show first name`
        : `${u.email}: win cards will be anonymized (Bonbot ****####)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleTopstep = async (u) => {
    const next = !u.topstep_enabled;
    try {
      await api.post(`/admin/users/${u.id}/topstep-toggle`, { enabled: next });
      toast.success(next
        ? `${u.email} — TopStep enabled (they'll see the futures panel next login)`
        : `${u.email} — TopStep disabled (existing creds preserved, UI hidden)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const startCleanup = async () => {
    if (!window.confirm("MetaApi cleanup\n\nThis scans your master MetaApi token and removes:\n  • Duplicate accounts (same login+server) — keeps the deployed one\n  • Orphan accounts not referenced by any user in the DB\n\nThis frees up MetaApi resource slots (saves $). Runs in background — takes ~30–90s.\n\nContinue?")) return;
    setCleanupBusy(true);
    try {
      await api.post("/admin/cleanup-metaapi-orphans");
      toast.success("Cleanup started — polling for results…");
      const tick = async () => {
        try {
          const { data } = await api.get("/admin/cleanup-metaapi-orphans/status");
          setCleanupStatus(data);
          if (data.running) setTimeout(tick, 5000);
          else {
            setCleanupBusy(false);
            const r = data.result || {};
            toast.success(`Cleanup done · scanned ${r.scanned}, kept ${(r.kept || []).length}, removed ${(r.removed || []).length}`);
          }
        } catch { setCleanupBusy(false); }
      };
      setTimeout(tick, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setCleanupBusy(false);
    }
  };

  const saveDefaultStrategy = async (force) => {
    if (!defaultStrategy) return;
    setLoading(true);
    try {
      await api.post("/admin/default-strategy", {
        strategy_id: defaultStrategy,
        force_apply_to_existing: force,
      });
      toast.success(force ? "Forced default strategy on all users" : "Default strategy updated");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const saveTelegram = async (patch) => {
    setTgSaving(true);
    try {
      const { data } = await api.put("/admin/telegram", patch);
      setTg(data);
      toast.success("Telegram settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setTgSaving(false); }
  };

  const testTelegram = async () => {
    if (!tg?.channel_id) {
      toast.error("Enter and save a channel ID first");
      return;
    }
    setTgTesting(true);
    try {
      const { data } = await api.post("/admin/telegram/test", {});
      toast.success(`Test sent · bot @${data.bot} → ${data.chat_id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
    finally { setTgTesting(false); }
  };

  const sendSampleCard = async () => {
    if (!tg?.channel_id) {
      toast.error("Enter and save a channel ID first");
      return;
    }
    setTgTesting(true);
    try {
      await api.post("/admin/telegram/test-card", {});
      toast.success("Sample win-card sent to Telegram");
    } catch (e) { toast.error(e.response?.data?.detail || "Card send failed"); }
    finally { setTgTesting(false); }
  };

  const replayWin = async (u) => {
    if (!tg?.channel_id) {
      toast.error("Save a channel ID first");
      return;
    }
    try {
      const { data } = await api.post(`/admin/users/${u.id}/replay-last-win`, {});
      const pipsTxt = data.pips != null ? `+${data.pips} pips · ` : "";
      toast.success(`Sent · ${data.first_name} ${data.pair} · ${pipsTxt}+$${data.profit_usd.toFixed(2)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replay failed");
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.email}?\n\nThis removes the user, all trades, signals, and any MetaApi account.`)) {
      return;
    }
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${data.email} · ${data.trades_deleted} trades removed`);
      setSelectedIds((prev) => prev.filter((id) => id !== u.id));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAllTestAccounts = () => {
    const testIds = users
      .filter((u) => u.role !== "admin" && /^(test_|e2e_|uitest_|metaapi\.test|mt5\.test|modetest|paywall_|testflow|newtest)/i.test(u.email || ""))
      .map((u) => u.id);
    setSelectedIds(testIds);
    if (testIds.length === 0) toast.info("No test accounts matched the pattern");
    else toast.success(`Selected ${testIds.length} test account(s)`);
  };

  const clearSelection = () => setSelectedIds([]);

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} user account(s)? This is permanent.`)) {
      return;
    }
    let deleted = 0;
    let failed = 0;
    for (const id of selectedIds) {
      try {
        await api.delete(`/admin/users/${id}`);
        deleted++;
      } catch { failed++; }
    }
    toast.success(`Bulk delete · ${deleted} removed${failed ? ` · ${failed} failed` : ""}`);
    setSelectedIds([]);
    load();
  };

  const handleLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      {/* Top bar */}
      <header className="border-b border-white/10 sticky top-0 bg-[#09090B]/95 backdrop-blur z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LineChart className="w-5 h-5 text-rose-400" strokeWidth={1.5} />
            <div>
              <div className="font-mono text-xs sm:text-sm tracking-widest uppercase">BonBot · Admin</div>
              <div className="text-[10px] text-zinc-500 uppercase tracking-widest">Back office</div>
            </div>
          </div>
          <button data-testid="admin-logout" onClick={handleLogout}
                  className="text-zinc-400 hover:text-rose-400 flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest">
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6" data-testid="admin-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          <Stat label="Total users" value={stats?.total_users ?? "—"} testId="admin-stat-users" />
          <Stat label="Paused" value={stats?.paused ?? "—"} tone={stats?.paused > 0 ? "rose" : null} testId="admin-stat-paused" />
          <Stat label="Subscribed" value={stats?.subscribed ?? "—"} tone="teal" testId="admin-stat-subscribed" />
          <Stat label="Pending payment" value={stats?.pending_payment ?? "—"} tone={stats?.pending_payment > 0 ? "rose" : null} testId="admin-stat-pending" />
          <Stat label="Brokers connected" value={stats?.connected_brokers ?? "—"} tone="teal" testId="admin-stat-connected" />
          <Stat label="PnL · 24h (all users)"
                value={stats ? `${stats.pnl_24h_global >= 0 ? "+" : ""}${stats.pnl_24h_global.toFixed(2)}` : "—"}
                tone={(stats?.pnl_24h_global ?? 0) >= 0 ? "teal" : "rose"}
                sub={`${stats?.trades_24h_global ?? 0} closed`}
                testId="admin-stat-pnl24" />
        </div>

        {/* Default strategy selector */}
        <div className="border border-white/10 bg-[#121214] rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Global default strategy</div>
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <select data-testid="admin-default-strategy-select"
                    value={defaultStrategy} onChange={(e) => setDefaultStrategy(e.target.value)}
                    className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm font-mono rounded-sm focus:border-zinc-500 focus:outline-none">
              <option value="">— pick a strategy —</option>
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>{s.id} — {s.name}</option>
              ))}
            </select>
            <button data-testid="admin-save-default-strategy" onClick={() => saveDefaultStrategy(false)} disabled={loading || !defaultStrategy}
                    className="bg-white/5 text-zinc-200 border border-white/10 hover:bg-white/10 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm">
              Save default
            </button>
            <button data-testid="admin-force-default-strategy" onClick={() => saveDefaultStrategy(true)} disabled={loading || !defaultStrategy}
                    className="bg-rose-400/10 text-rose-400 border border-rose-400/30 hover:bg-rose-400/20 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm">
              Force on all
            </button>
          </div>
          <p className="text-[11px] text-zinc-600 mt-3">
            "Save default" applies to NEW users only. "Force on all" overrides every existing non-admin user's strategy too.
          </p>
        </div>

        {/* MetaApi maintenance */}
        <div className="border border-amber-400/20 bg-[#121214] rounded-sm p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400">MetaApi maintenance</div>
              <p className="text-xs text-zinc-400 mt-1.5">
                Scan your master MetaApi token and remove duplicate / orphan accounts. Frees resource slots and trims billing.
              </p>
            </div>
            <button data-testid="admin-cleanup-metaapi" onClick={startCleanup} disabled={cleanupBusy}
                    className="bg-amber-400/10 text-amber-400 border border-amber-400/30 hover:bg-amber-400/20 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm shrink-0">
              {cleanupBusy ? "Scanning…" : "Cleanup MetaApi accounts"}
            </button>
          </div>
          {cleanupStatus?.result && (
            <div className="mt-3 text-xs font-mono text-zinc-400" data-testid="admin-cleanup-result">
              <div>// Scanned: {cleanupStatus.result.scanned} · Kept: {(cleanupStatus.result.kept || []).length} · Removed: <span className="text-rose-400">{(cleanupStatus.result.removed || []).length}</span></div>
              {(cleanupStatus.result.errors || []).length > 0 && (
                <div className="text-rose-400 mt-1">Errors: {cleanupStatus.result.errors.length}</div>
              )}
            </div>
          )}
        </div>

        {/* Fire trade for ALL users */}
        <div className="border border-rose-500/30 bg-[#121214] rounded-sm p-4" data-testid="fire-all-card">
          <div className="flex items-center gap-2 mb-1">
            <Flame className="w-4 h-4 text-rose-400" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-widest text-rose-400">Fire trade for ALL users</div>
          </div>
          <p className="text-xs text-zinc-400 mt-1.5 mb-3">
            Place the same pair + side trade across every eligible user in parallel.
            Each user uses THEIR OWN lot size, SL/TP, spread buffer, and active strategy.
            Users that are paused, unpaid, on rejection-wick, or whose monitored pairs don&apos;t
            include the pair will be silently skipped.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Pair</div>
              <select data-testid="admin-fire-all-pair" value={fireAllPair}
                      onChange={(e) => setFireAllPair(e.target.value)}
                      className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-rose-400/50 focus:outline-none">
                {["XAU_USD","USD_JPY","EUR_USD","GBP_USD","AUD_USD","USD_CAD","NZD_USD","USD_CHF","EUR_JPY","GBP_JPY","XAG_USD","BTC_USD","ETH_USD","US30","US100","US500"].map(p =>
                  <option key={p} value={p}>{p.replace("_","")}</option>
                )}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Side</div>
              <select data-testid="admin-fire-all-side" value={fireAllSide}
                      onChange={(e) => setFireAllSide(e.target.value)}
                      className="w-full bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-rose-400/50 focus:outline-none">
                <option value="BUY">BUY</option>
                <option value="SELL">SELL</option>
              </select>
            </div>
            <div className="sm:col-span-2 flex items-end">
              <button data-testid="admin-fire-all-btn" onClick={fireForAll} disabled={fireAllBusy}
                      className="w-full bg-rose-400/10 text-rose-300 border border-rose-400/40 hover:bg-rose-400/20 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm flex items-center justify-center gap-2">
                <Zap className="w-3.5 h-3.5" />
                {fireAllBusy ? "Firing…" : `Fire ${fireAllSide} on ${fireAllPair.replace("_","")} for ALL`}
              </button>
            </div>
          </div>
          {fireAllResult && (
            <div data-testid="admin-fire-all-result"
                 className="mt-3 border border-white/5 bg-black/30 p-3 rounded-sm text-[11px]">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-2">
                <Stat label="Total" value={fireAllResult.total_users} />
                <Stat label="Fired" value={fireAllResult.fired} tone="teal" />
                <Stat label="Skipped" value={fireAllResult.skipped} tone="amber" />
                <Stat label="Failed" value={fireAllResult.failed} tone={fireAllResult.failed > 0 ? "rose" : null} />
              </div>
              {fireAllResult.details?.failed?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-rose-400 cursor-pointer">Failed users ({fireAllResult.details.failed.length})</summary>
                  <ul className="mt-1 space-y-0.5 pl-3">
                    {fireAllResult.details.failed.map((r,i) => (
                      <li key={i} className="text-zinc-400">
                        <span className="text-zinc-200">{r.email}</span>: <span className="text-rose-400/80">{r.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
              {fireAllResult.details?.skipped?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-amber-400 cursor-pointer">Skipped users ({fireAllResult.details.skipped.length})</summary>
                  <ul className="mt-1 space-y-0.5 pl-3 max-h-40 overflow-auto">
                    {fireAllResult.details.skipped.map((r,i) => (
                      <li key={i} className="text-zinc-400">
                        <span className="text-zinc-200">{r.email}</span>: <span className="text-amber-400/80">{r.reason}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Telegram broadcast settings */}
        <div className="border border-sky-400/20 bg-[#121214] rounded-sm p-4" data-testid="admin-telegram-panel">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Send className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
              <div className="text-[10px] uppercase tracking-widest text-sky-400">Telegram broadcast</div>
            </div>
            <div className="text-[10px] font-mono text-zinc-500">
              {tg?.bot_token_configured ? <span className="text-teal-400">bot token: ✓ configured</span> : <span className="text-rose-400">bot token: ✗ not set</span>}
            </div>
          </div>
          {!tg ? (
            <div className="text-xs text-zinc-500">Loading…</div>
          ) : (
            <div className="space-y-4">
              {/* Channel ID input */}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Channel ID</div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    data-testid="admin-tg-channel-input"
                    type="text"
                    placeholder="@your_channel or -100123456789"
                    value={tg.channel_id || ""}
                    onChange={(e) => setTg({ ...tg, channel_id: e.target.value })}
                    className="flex-1 bg-black border border-white/10 rounded-sm px-3 py-2 text-sm font-mono focus:border-sky-400/50 focus:outline-none"
                  />
                  <button
                    data-testid="admin-tg-save-channel"
                    onClick={() => saveTelegram({ channel_id: tg.channel_id || "" })}
                    disabled={tgSaving}
                    className="bg-sky-400/10 text-sky-400 border border-sky-400/30 hover:bg-sky-400/20 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm shrink-0">
                    {tgSaving ? "Saving…" : "Save channel"}
                  </button>
                  <button
                    data-testid="admin-tg-test-button"
                    onClick={testTelegram}
                    disabled={tgTesting || !tg.channel_id}
                    className="bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm shrink-0">
                    {tgTesting ? "Sending…" : "Send test"}
                  </button>
                  <button
                    data-testid="admin-tg-sample-card-button"
                    onClick={sendSampleCard}
                    disabled={tgTesting || !tg.channel_id}
                    className="bg-teal-400/10 text-teal-400 border border-teal-400/30 hover:bg-teal-400/20 px-4 py-2 text-xs font-mono uppercase tracking-widest disabled:opacity-50 rounded-sm shrink-0">
                    Preview card
                  </button>
                </div>
                <div className="text-[10px] text-zinc-600 mt-1.5">
                  Add the bot <code className="text-zinc-400">@Pockettrade509bot</code> to your channel as an admin, then paste the channel @handle or numeric ID here.
                </div>
              </div>

              {/* Toggles */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-white/5">
                <ToggleRow
                  testId="admin-tg-toggle-signals"
                  label="Broadcast signals"
                  sub="Post entry / SL / TP text when a strategy fires"
                  active={!!tg.signals_enabled}
                  onChange={(v) => saveTelegram({ signals_enabled: v })}
                />
                <ToggleRow
                  testId="admin-tg-toggle-reports"
                  label="Trade reports (wins only)"
                  sub="Post pips won when a trade closes profitably. Silent on losses."
                  active={!!tg.reports_enabled}
                  onChange={(v) => saveTelegram({ reports_enabled: v })}
                />
                <ToggleRow
                  testId="admin-tg-toggle-daily"
                  label="End-of-day summary"
                  sub="Auto-posts win/loss/net pips at 23:55 UTC"
                  active={!!tg.daily_summary_enabled}
                  onChange={(v) => saveTelegram({ daily_summary_enabled: v })}
                />
              </div>
            </div>
          )}
        </div>

        {/* Users table */}
        <div className="border border-white/10 bg-[#121214] rounded-sm">
          <div className="border-b border-white/10 px-4 py-3 text-[10px] uppercase tracking-widest text-zinc-500 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <span>Users · {users.length}{selectedIds.length > 0 && <span className="text-rose-400"> · {selectedIds.length} selected</span>}</span>
            <div className="flex flex-wrap gap-2">
              <button data-testid="admin-select-test-accounts" onClick={selectAllTestAccounts}
                      className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-amber-400/30 text-amber-400 hover:bg-amber-400/10 rounded-sm">
                Select Test Accounts
              </button>
              {selectedIds.length > 0 && (
                <>
                  <button data-testid="admin-clear-selection" onClick={clearSelection}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-white/15 text-zinc-300 hover:bg-white/5 rounded-sm">
                    Clear
                  </button>
                  <button data-testid="admin-bulk-delete" onClick={bulkDelete}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-sm">
                    <Trash2 className="w-3 h-3 inline" /> Delete Selected ({selectedIds.length})
                  </button>
                </>
              )}
            </div>
          </div>
          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-white/10">
                  <th className="text-left py-2 pl-4 w-8">
                    <input type="checkbox" data-testid="admin-select-all"
                           checked={selectedIds.length > 0 && selectedIds.length === users.filter(u => u.role !== "admin").length}
                           onChange={(e) => {
                             if (e.target.checked) setSelectedIds(users.filter(u => u.role !== "admin").map(u => u.id));
                             else setSelectedIds([]);
                           }}
                           className="accent-rose-400 cursor-pointer" />
                  </th>
                  <th className="text-left py-2 px-4">User</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Subscription</th>
                  <th className="text-left">Broker</th>
                  <th className="text-left">Strategy</th>
                  <th className="text-right">Open</th>
                  <th className="text-right">PnL 24h</th>
                  <th className="text-center">Quota</th>
                  <th className="text-right pr-4">Actions</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {users.length === 0 && (
                  <tr><td colSpan={10} className="text-center text-zinc-500 py-8">No users yet</td></tr>
                )}
                {users.map((u) => (
                  <tr key={u.id} className={`border-b border-white/5 hover:bg-white/[0.02] ${selectedIds.includes(u.id) ? "bg-rose-500/5" : ""}`} data-testid={`admin-user-row-${u.id}`}>
                    <td className="py-2 pl-4 w-8">
                      {u.role !== "admin" && (
                        <input type="checkbox" data-testid={`admin-select-${u.id}`}
                               checked={selectedIds.includes(u.id)}
                               onChange={() => toggleSelected(u.id)}
                               className="accent-rose-400 cursor-pointer" />
                      )}
                    </td>
                    <td className="py-2 px-4">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-zinc-100 truncate">{u.email}</div>
                          <div className="text-[10px] text-zinc-500 truncate">{u.name || ""} · {u.default_mt5_login || "—"}</div>
                        </div>
                        <button
                          data-testid={`admin-topstep-toggle-${u.id}`}
                          onClick={() => toggleTopstep(u)}
                          title={u.topstep_enabled
                            ? "TopStep enabled — click to disable"
                            : "Click to enable TopStep futures for this user"}
                          className={`shrink-0 text-[9px] font-mono uppercase tracking-widest px-1.5 py-1 border rounded-sm inline-flex items-center gap-1 ${
                            u.topstep_enabled
                              ? "border-amber-400/50 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
                              : "border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/20"
                          }`}
                        >
                          <Zap className="w-2.5 h-2.5" />
                          TopStep{u.topstep_enabled ? (u.topstep_connected ? " ✓" : " ●") : ""}
                        </button>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 flex-wrap">
                        {u.paused
                          ? <span className="text-[9px] bg-rose-400/10 text-rose-400 border border-rose-400/30 px-1.5 py-0.5 uppercase tracking-widest">paused</span>
                          : <span className="text-[9px] bg-teal-400/10 text-teal-400 border border-teal-400/30 px-1.5 py-0.5 uppercase tracking-widest">active</span>}
                        {u.verified
                          ? <span className="text-[9px] bg-white/5 text-zinc-300 border border-white/10 px-1.5 py-0.5 uppercase tracking-widest">verified</span>
                          : <span className="text-[9px] bg-amber-400/10 text-amber-400 border border-amber-400/30 px-1.5 py-0.5 uppercase tracking-widest">unverified</span>}
                      </div>
                    </td>
                    <td>
                      <select
                        data-testid={`admin-sub-${u.id}`}
                        value={u.subscription_status || "none"}
                        onChange={(e) => setSubscription(u, e.target.value)}
                        className={`bg-black border px-2 py-1 text-[11px] font-mono rounded-sm focus:outline-none ${
                          (u.subscription_status === "active" || u.subscription_status === "free")
                            ? "border-teal-400/30 text-teal-400"
                            : u.subscription_status === "pending"
                              ? "border-amber-400/30 text-amber-400"
                              : "border-white/10 text-zinc-500"
                        }`}
                      >
                        <option value="none">None</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active (paid)</option>
                        <option value="free">Free (lifetime)</option>
                      </select>
                    </td>
                    <td className={u.broker_connected ? "text-teal-400 text-xs" : "text-zinc-500 text-xs"}>
                      {u.broker_connected ? "● connected" : "○ none"}
                    </td>
                    <td className="text-xs text-zinc-400">{u.strategy_id || "—"}</td>
                    <td className="text-right tabular-nums">{u.open_positions}</td>
                    <td className={`text-right tabular-nums ${u.pnl_24h >= 0 ? "text-teal-400" : "text-rose-400"}`}>
                      {u.pnl_24h >= 0 ? "+" : ""}{u.pnl_24h.toFixed(2)}
                    </td>
                    <td className="text-center">
                      <div className="flex items-center gap-1 justify-center">
                        <button data-testid={`admin-quota-dec-${u.id}`} onClick={() => updateQuota(u, u.account_quota - 1)}
                                disabled={u.account_quota <= 0}
                                className="w-6 h-6 text-zinc-400 hover:text-zinc-100 border border-white/10 hover:bg-white/5 rounded-sm disabled:opacity-30">−</button>
                        <span data-testid={`admin-quota-val-${u.id}`} className="w-6 text-center text-zinc-100">{u.account_quota}</span>
                        <button data-testid={`admin-quota-inc-${u.id}`} onClick={() => updateQuota(u, u.account_quota + 1)}
                                disabled={u.account_quota >= 10}
                                className="w-6 h-6 text-zinc-400 hover:text-zinc-100 border border-white/10 hover:bg-white/5 rounded-sm disabled:opacity-30">+</button>
                      </div>
                    </td>
                    <td className="text-right pr-4">
                      <div className="flex justify-end items-center gap-2">
                        <button data-testid={`admin-replay-win-${u.id}`}
                                onClick={() => replayWin(u)}
                                title="Replay last winning trade to Telegram"
                                className="text-xs font-mono uppercase tracking-widest px-2 py-1 border border-sky-400/30 text-sky-400 hover:bg-sky-400/10 rounded-sm">
                          <Send className="w-3 h-3 inline" /> Win
                        </button>
                        <button data-testid={`admin-edit-settings-${u.id}`}
                                onClick={() => setSettingsTarget(u)}
                                title="Edit strategy & risk settings for this user"
                                className={`text-xs font-mono uppercase tracking-widest px-2 py-1 border rounded-sm ${
                                  u.settings_locked
                                    ? "border-amber-400/50 text-amber-300 bg-amber-400/10 hover:bg-amber-400/20"
                                    : "border-white/15 text-zinc-300 hover:bg-white/5"
                                }`}>
                          {u.settings_locked
                            ? <><Lock className="w-3 h-3 inline" /> Locked</>
                            : <><Sliders className="w-3 h-3 inline" /> Settings</>}
                        </button>
                        <button data-testid={`admin-name-toggle-${u.id}`}
                                onClick={() => toggleCertificateName(u)}
                                title={u.show_name_on_certificate
                                  ? "Win cards show this user's first name — click to anonymize"
                                  : "Win cards anonymized as Bonbot ****1234 — click to show first name"}
                                className={`text-xs font-mono uppercase tracking-widest px-2 py-1 border rounded-sm ${
                                  u.show_name_on_certificate
                                    ? "border-teal-400/40 text-teal-300 bg-teal-400/10 hover:bg-teal-400/20"
                                    : "border-white/15 text-zinc-400 hover:bg-white/5"
                                }`}>
                          {u.show_name_on_certificate
                            ? <><User className="w-3 h-3 inline" /> Name</>
                            : <><UserX className="w-3 h-3 inline" /> Anon</>}
                        </button>
                        <button data-testid={`admin-pause-${u.id}`} onClick={() => togglePause(u)} disabled={loading}
                                className={`text-xs font-mono uppercase tracking-widest px-2 py-1 border rounded-sm ${u.paused ? "border-teal-400/30 text-teal-400 hover:bg-teal-400/10" : "border-rose-400/30 text-rose-400 hover:bg-rose-400/10"}`}>
                          {u.paused ? <Play className="w-3 h-3 inline" /> : <Pause className="w-3 h-3 inline" />}
                          {" "}{u.paused ? "Resume" : "Pause"}
                        </button>
                        {!u.verified && (
                          <button data-testid={`admin-verify-${u.id}`} onClick={() => forceVerify(u)}
                                  className="text-xs font-mono uppercase tracking-widest px-2 py-1 border border-amber-400/30 text-amber-400 hover:bg-amber-400/10 rounded-sm">
                            <ShieldCheck className="w-3 h-3 inline" /> Verify
                          </button>
                        )}
                        <button data-testid={`admin-delete-${u.id}`} onClick={() => deleteUser(u)}
                                title="Delete user permanently"
                                className="text-xs font-mono uppercase tracking-widest px-2 py-1 border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 rounded-sm">
                          <Trash2 className="w-3 h-3 inline" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-white/5">
            {users.length === 0 && (
              <div className="text-center text-zinc-500 py-8 text-sm">No users yet</div>
            )}
            {users.map((u) => (
              <div key={u.id} className="p-3 font-mono" data-testid={`admin-user-row-m-${u.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <div className="text-sm text-zinc-100 truncate">{u.email}</div>
                      <button
                        data-testid={`admin-topstep-toggle-m-${u.id}`}
                        onClick={() => toggleTopstep(u)}
                        className={`shrink-0 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border rounded-sm inline-flex items-center gap-1 ${
                          u.topstep_enabled
                            ? "border-amber-400/50 bg-amber-400/10 text-amber-300"
                            : "border-white/10 text-zinc-500"
                        }`}
                      >
                        <Zap className="w-2.5 h-2.5" />
                        {u.topstep_enabled ? "ON" : "OFF"}
                      </button>
                    </div>
                    <div className="text-[10px] text-zinc-500">{u.name || ""} · login {u.default_mt5_login || "—"}</div>
                  </div>
                  <div className={`text-sm tabular-nums shrink-0 ${u.pnl_24h >= 0 ? "text-teal-400" : "text-rose-400"}`}>
                    {u.pnl_24h >= 0 ? "+" : ""}{u.pnl_24h.toFixed(2)}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-2">
                  {u.paused
                    ? <span className="text-[9px] bg-rose-400/10 text-rose-400 border border-rose-400/30 px-1.5 py-0.5 uppercase tracking-widest">paused</span>
                    : <span className="text-[9px] bg-teal-400/10 text-teal-400 border border-teal-400/30 px-1.5 py-0.5 uppercase tracking-widest">active</span>}
                  {u.verified
                    ? <span className="text-[9px] bg-white/5 text-zinc-300 border border-white/10 px-1.5 py-0.5 uppercase tracking-widest">verified</span>
                    : <span className="text-[9px] bg-amber-400/10 text-amber-400 border border-amber-400/30 px-1.5 py-0.5 uppercase tracking-widest">unverified</span>}
                  <span className="text-[9px] text-zinc-400">{u.strategy_id || "no strategy"}</span>
                  <span className="text-[9px] text-zinc-400">{u.open_positions} open</span>
                </div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <select
                    data-testid={`admin-sub-m-${u.id}`}
                    value={u.subscription_status || "none"}
                    onChange={(e) => setSubscription(u, e.target.value)}
                    className={`bg-black border px-2 py-1 text-[10px] font-mono rounded-sm focus:outline-none ${
                      (u.subscription_status === "active" || u.subscription_status === "free")
                        ? "border-teal-400/30 text-teal-400"
                        : u.subscription_status === "pending"
                          ? "border-amber-400/30 text-amber-400"
                          : "border-white/10 text-zinc-500"
                    }`}
                  >
                    <option value="none">None</option>
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="free">Free lifetime</option>
                  </select>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-zinc-500 uppercase">Quota:</span>
                    <button data-testid={`admin-quota-dec-m-${u.id}`} onClick={() => updateQuota(u, u.account_quota - 1)}
                            disabled={u.account_quota <= 0}
                            className="w-6 h-6 text-zinc-400 border border-white/10 rounded-sm disabled:opacity-30">−</button>
                    <span data-testid={`admin-quota-val-m-${u.id}`} className="w-6 text-center text-zinc-100">{u.account_quota}</span>
                    <button data-testid={`admin-quota-inc-m-${u.id}`} onClick={() => updateQuota(u, u.account_quota + 1)}
                            disabled={u.account_quota >= 10}
                            className="w-6 h-6 text-zinc-400 border border-white/10 rounded-sm disabled:opacity-30">+</button>
                  </div>
                  <button data-testid={`admin-pause-m-${u.id}`} onClick={() => togglePause(u)} disabled={loading}
                          className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border rounded-sm ${u.paused ? "border-teal-400/30 text-teal-400" : "border-rose-400/30 text-rose-400"}`}>
                    {u.paused ? "Resume" : "Pause"}
                  </button>
                  <button data-testid={`admin-replay-win-m-${u.id}`} onClick={() => replayWin(u)}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-sky-400/30 text-sky-400 rounded-sm">
                    📤 Win
                  </button>
                  <button data-testid={`admin-delete-m-${u.id}`} onClick={() => deleteUser(u)}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-rose-500/40 text-rose-400 rounded-sm">
                    🗑 Delete
                  </button>
                  {!u.verified && (
                    <button data-testid={`admin-verify-m-${u.id}`} onClick={() => forceVerify(u)}
                            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-amber-400/30 text-amber-400 rounded-sm">
                      Verify
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Per-user settings modal */}
      {settingsTarget && (
        <ErrorBoundary onClose={() => setSettingsTarget(null)}>
          <UserSettingsModal
            user={settingsTarget}
            strategies={strategies}
            onClose={() => setSettingsTarget(null)}
            onSaved={load}
          />
        </ErrorBoundary>
      )}
    </div>
  );
}

function Stat({ label, value, sub, tone, testId }) {
  const color = tone === "teal" ? "text-teal-400" : tone === "rose" ? "text-rose-400" : "text-zinc-100";
  return (
    <div className="border border-white/10 bg-[#121214] rounded-sm p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`text-xl sm:text-2xl font-mono mt-1 tabular-nums ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-zinc-600 mt-1">{sub}</div>}
    </div>
  );
}


function ToggleRow({ label, sub, active, onChange, testId }) {
  return (
    <div className={`border rounded-sm p-3 transition ${active ? "border-sky-400/40 bg-sky-400/5" : "border-white/10 bg-black/40"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-200">{label}</div>
          <div className="text-[10px] text-zinc-500 mt-1 leading-snug">{sub}</div>
        </div>
        <button
          data-testid={testId}
          onClick={() => onChange(!active)}
          className={`relative shrink-0 w-10 h-5 rounded-full transition-colors focus:outline-none ${active ? "bg-sky-400" : "bg-zinc-700"}`}
          aria-pressed={active}
        >
          <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${active ? "translate-x-5" : ""}`} />
        </button>
      </div>
    </div>
  );
}
